import { EMAIL_LINK } from "@config/link";
import routeConfig from "@config/routes";
import {
  ChevronRight,
  CloudOffOutlined,
  Email,
  EmojiEventsOutlined,
  ExpandMore,
  LockOutlined,
  OpenInNew,
  SchoolOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Link as MuiLink,
  type SxProps,
  type Theme,
  useMediaQuery,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import type { ApplicationState } from "@store/index";
import useLanguageHook from "containers/hooks/useLanguageHook";
import { useAppSelector } from "index";
import React from "react";
import { Link } from "react-router-dom";

const asset = (name: string) => new URL(`../../../public/${name}`, import.meta.url).href;

const AtheneSVG = asset("athene.svg");
const IgdSVG = asset("igd.svg");
const TudSVG = asset("tud.svg");

// TODO: implement route
const DEMO_ROUTE = `${routeConfig.Application}/data/demo`;

const HERO_POSTER = asset("preview.png");
// TODO: add video
const HERO_MP4 = asset("hero.mp4");
//const HERO_WEBM = asset("hero.webm");
const IVA_URL = `https://www.iva.igd.fraunhofer.de/`;

const ctaSx: SxProps<Theme> = {
  textTransform: "none",
  fontSize: "1.2rem",
  fontWeight: 500,
  color: "white",
  boxShadow: "0px 4px 8px 4px rgba(0, 0, 0, 0.25)",
};

const sectionSx: SxProps<Theme> = { py: { xs: 6, md: 10 }, scrollMarginTop: "64px" };

type Publication = {
  image: string;
  title: string;
  conf: string;
  year: string;
  authors: string;
  url: string;
  bibtex: string;
  video?: string;
  preprinturl?: string;
  award?: string[];
  videourl?: string;
};

const PUBLICATIONS: Publication[] = [
  {
    authors: "Marija Schufrin, Steven Lamarr Reynolds, Arjan Kuijper, Jörn Kohlhammer",
    year: "2021",
    title: "A Visualization Interface to Improve the Transparency of Collected Personal Data on the Internet",
    conf: "IEEE Transactions on Visualization and Computer Graphics 27(2)",
    url: "https://doi.org/10.1109/TVCG.2020.3028946",
    preprinturl: "https://arxiv.org/pdf/2009.02998.pdf",
    award: ["Best Paper, VizSec 2020", "Best Paper – Impact on Society, Fraunhofer IGD 2021"],
    video: "20 min",
    videourl: "https://youtu.be/sbJzQvOD62E?t=4415",
    image: asset("20-01.png"),
    bibtex: `@article{schufrin2020visualization,
  author={Schufrin, Marija and Reynolds, Steven Lamarr and Kuijper, Arjan and Kohlhammer, J{"o}rn},
  journal={IEEE Transactions on Visualization and Computer Graphics},
  title={{A Visualization Interface to Improve the Transparency of Collected Personal Data on the Internet}},year={2021},
  volume={27},
  number={2},
  pages={1840-1849},
  abstract={Online services are used for all kinds of activities, like news, entertainment, publishing content or connecting with others. But information technology enables new threats to privacy by means of global mass surveillance, vast databases and fast distribution networks. Current news are full of misuses and data leakages. In most cases, users are powerless in such situations and develop an attitude of neglect for their online behaviour. On the other hand, the GDPR (General Data Protection Regulation) gives users the right to request a copy of all their personal data stored by a particular service, but the received data is hard to understand or analyze by the common internet user. This paper presents TransparencyVis - a web-based interface to support the visual and interactive exploration of data exports from different online services. With this approach, we aim at increasing the awareness of personal data stored by such online services and the effects of online behaviour. This design study provides an online accessible prototype and a best practice to unify data exports from different sources.},
  keywords={},
  doi={10.1109/TVCG.2020.3028946},
  ISSN={1941-0506},
  month={Feb},
}
`,
  },
  {
    authors: "Hervais Simo, Haya Shulman, Marija Schufrin, Steven Lamarr Reynolds, Jörn Kohlhammer",
    year: "2021",
    title: "PrivInferVis: Towards Enhancing Transparency over Attribute Inference in Online Social Networks",
    conf: "INFOCOM Workshops 2021",
    url: "https://doi.org/10.1109/INFOCOMWKSHPS51825.2021.9484595",
    image: asset("20-02.png"),
    bibtex: `@inproceedings{simo2021privinfervis,
  author={Simo, Hervais and Shulman, Haya and Schufrin, Marija and Reynolds, Steven Lamarr and Kohlhammer, J{"o}rn},
  booktitle={IEEE INFOCOM 2021 - IEEE Conference on Computer Communications Workshops (INFOCOM WKSHPS)}, 
  title={PrivInferVis: Towards Enhancing Transparency over Attribute Inference in Online Social Networks}, 
  year={2021},
  volume={},
  number={},
  pages={1-2},
  abstract={The European GDPR calls, besides other things, for innovative tools to empower online social networks (OSN) users with transparency over risks of attribute inferences. In this work, we propose a novel transparency-enhancing framework for OSN, PrivInferVis, to help people assess and visualize their individual risks of attribute inference derived from public details from their social graphs in different OSN domains. We propose a weighted Bayesian model as the underlying method for attribute inference. A preliminary evaluation shows that our proposal outperforms baseline algorithms on several evaluation metrics significantly. PrivInferVis provides visual interfaces that allow users to explore details about their (inferred and self-disclosed) data and to understand how inference estimates and related scores are derived.},
  keywords={},
  doi={10.1109/INFOCOMWKSHPS51825.2021.9484595},
  ISSN={},
  month={May}
}`,
  },
  {
    authors: "Marija Dutz, Nataša Starčević, Steven Lamarr Reynolds, Jörn Kohlhammer",
    year: "2023",
    title: "Exploring the Design of Visualizations of Personal Online Data Based on Users' Mental Models",
    conf: "2023 27th International Conference Information Visualisation (IV)",
    url: "https://doi.org/10.1109/IV60283.2023.00029",
    image: asset("23-01.png"),
    bibtex: `@INPROCEEDINGS{dutz2023exploring,
  author={Dutz, Marija and Starčević, Nataša and Reynolds, Steven Lamarr and Kohlhammer, J{"o}rn},
  booktitle={2023 27th International Conference Information Visualisation (IV)},
  title={Exploring the Design of Visualizations of Personal Online Data Based on Users' Mental Models},
  year={2023},
  volume={},
  number={},
  pages={119-123},
  abstract={As data becomes more and more pervasive in our daily lives, supporting people in getting visual insights into their data is an important challenge to address. However, as data visualization literacy is still low, a gap between designers' mental model and users is not uncommon. To best pick up users where they are, we propose to include the data mental models of users into the design process. In this paper, we present our investigations in this direction by incorporating user sketches of their idea about their personal data stored at online services as a basis of our designs. We present our design study on personal data visualization interfaces resulting in a set of user sketches for three types of user groups and in three visualization interfaces. Finally, we reflect on our learnings and identify pitfalls to support other researchers in applying similar approaches.},
  doi={10.1109/IV60283.2023.00029}
}`,
  },
  {
    authors: "S. L. Reynolds, J. Stromberg, H. Lücke-Tieke,",
    year: "2025",
    title: "Interactive Integration of Heterogeneous Datasets for Analytical Tasks",
    conf: "29th International Conference Information Visualisation (IV)",
    url: "https://doi.org/10.1109/IV68685.2025.00054",
    image: asset("25-01.png"),
    bibtex: `@inproceedings{reynoldsInteractiveIntegrationHeterogeneous2025,
  title = {Interactive {{Integration}} of {{Heterogeneous Datasets}} for {{Analytical Tasks}}},
  booktitle = {2025 29th {{International Conference Information Visualisation}} ({{IV}})},
  author = {Reynolds, Steven Lamarr and Stromberg, Jonas and Lücke-Tieke, Hendrik and May, Thorsten and Kohlhammer, Jörn},
  date = {2025-08-05},
  pages = {260--265},
  publisher = {IEEE},
  location = {Darmstadt, Germany},
  doi = {10.1109/IV68685.2025.00054},
  url = {https://ieeexplore.ieee.org/document/11216798/},
  urldate = {2026-04-29},
  abstract = {Data science is integral. Its importance continues to grow, and so does the need for adequate tools to integrate multiple datasets and audit their transformations. In rapidly evolving fields where data formats frequently change, this task is often performed manually. However, manual data-wrangling tasks are often error-prone and time-consuming for human analysts. In this paper, we propose a semi-automatic data wrangling approach that allows analysts to interactively integrate heterogeneous structured datasets into a unified target data format. This is achieved by abstracting raw data into schemas and transforming relevant attributes into a target data schema. To assist analysts, it also suggests an initial transformation and visualizes the resulting data to verify the transformation. We also provide a use case to demonstrate the capabilities of our interface for wrangling and verification. Supplemental materials are available at https: //osf.io/dscfb/?view only=7b50be799c8540eaaf50e5b296629530. Index Terms—Interactive visualization, data transformation, data integration, verification, usability in security and privacy.},
  eventtitle = {2025 29th {{International Conference Information Visualisation}} ({{IV}})},
  isbn = {979-8-3315-7741-4},
  langid = {english},
}
`,
  },
];

const THESES = [
  "Steven Lamarr Reynolds. Visualization Interface to Improve the Transparency of Collected Personal Data on the Internet. TU Darmstadt, Master's thesis, 2019.",
  "Nataša Starčević. Visualizing Online Personal Data based on the Mental Models of Common Internet User Types. TU Darmstadt, Master's thesis, 2021.",
  "Julien Rene Schröter. User-Guided Visual Interactive Interface to extract Data from Personal Data Exports. TU Darmstadt, Bachelor thesis, 2021.",
  "Andrés Enrique Fischer. Designstudie über eine visuelle Schnittstelle von personenbezogenen Daten für Laien. TU Darmstadt, Master's thesis, 2022.",
  "Maximilian Wilhelm Gerk. Design Studie eines Gaming-Ansatzes für die visuelle Analyse persönlicher privater Daten. TU Darmstadt, Master's thesis, 2022.",
  "Ali Khesrau Jabhe. Dynamic Generation of a Visual Storytelling Approach for Personal Data. TU Darmstadt, Master's thesis, 2023.",
  "Jan Erik Koch. Design Study on Responsive Visualizations of Personal Data disclosed on the Internet. TU Darmstadt, Master's thesis, 2023.",
  "Jonathan Schad. Spatiotemporal Visualization of Personal Data Exports to Increase Privacy Awareness. TU Darmstadt, Master's thesis, 2023.",
];

// Decorative hero image.
const HeroMedia = () => {
  const reduceMotion = useMediaQuery(`(prefers-reduced-motion: reduce)`);
  const frameSx = {
    display: "block",
    width: "100%",
    borderRadius: 2,
    aspectRatio: "16 / 9",
    objectFit: "cover",
  } as const;

  return (
    <Box aria-hidden sx={{ maxWidth: 860, mx: "auto", p: 1, borderRadius: 3, bgcolor: "rgba(255,255,255,0.12)" }}>
      {reduceMotion ? (
        <Box component="img" src={HERO_POSTER} alt="" sx={frameSx} />
      ) : (
        <Box component="video" autoPlay loop muted playsInline preload="metadata" poster={HERO_POSTER} sx={frameSx}>
          {/*<source src={HERO_WEBM} type="video/webm" />*/}
          <source src={HERO_MP4} type="video/mp4" />
        </Box>
      )}
    </Box>
  );
};

const CiteButton = ({ text, label = "Copy BibTeX" }: { text: string; label?: string }) => {
  const [copied, setCopied] = React.useState(false);
  return (
    <Button
      size="small"
      variant="outlined"
      onClick={() => {
        navigator.clipboard.writeText(text).then(
          () => setCopied(true),
          () => setCopied(false),
        );
      }}
    >
      {copied ? "Copied" : label}
    </Button>
  );
};

const VizCard = ({ title, text, image }: { title: string; text: string; image: string }) => (
  <Grid item xs={12} sm={6} md={4} component="figure" sx={{ m: 0 }}>
    <Paper elevation={1} sx={{ height: "100%", overflow: "hidden" }}>
      <Box
        component="img"
        src={image}
        alt={title}
        sx={{ display: "block", width: "100%", aspectRatio: "16 / 10", objectFit: "cover" }}
      />

      <Box component="figcaption" sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {text}
        </Typography>
      </Box>
    </Paper>
  </Grid>
);

const PublicationEntry = ({ paper }: { paper: Publication }) => {
  const [showBibtex, setShowBibtex] = React.useState(false);

  return (
    <Stack sx={{ pt: 4 }} direction="row" spacing={2}>
      <Box sx={{ display: { xs: "none", md: "block" }, flex: "0 0 142px" }}>
        <a target="_blank" rel="noopener noreferrer" href={paper.url}>
          <img
            alt={`First page of “${paper.title}”`}
            src={paper.image}
            style={{ border: "1px solid #eee", width: "142px" }}
          />
        </a>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography gutterBottom>
          {paper.authors} ({paper.year}). <b>{paper.title}</b>. <i>{paper.conf}</i>.{" "}
          <MuiLink href={paper.url} target="_blank" rel="noopener noreferrer">
            {paper.url}
          </MuiLink>
        </Typography>
        {paper.award?.map((a) => (
          <Chip key={a} size="small" icon={<EmojiEventsOutlined />} label={a} sx={{ mr: 1, mb: 1 }} />
        ))}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {paper.preprinturl && (
            <Button
              size="small"
              href={paper.preprinturl}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNew fontSize="small" />}
            >
              PDF
            </Button>
          )}
          {paper.videourl && (
            <Button
              size="small"
              href={paper.videourl}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNew fontSize="small" />}
            >
              {`Video (${paper.video})`}
            </Button>
          )}
          <Button size="small" onClick={() => setShowBibtex(!showBibtex)}>
            Show BibTeX
          </Button>
          <CiteButton text={paper.bibtex} label="Copy BibTeX" />
        </Stack>
        <Collapse in={showBibtex}>
          <Paper elevation={4} sx={{ p: 1, mt: 1 }}>
            <Typography variant="code">{paper.bibtex}</Typography>
          </Paper>
        </Collapse>
      </Box>
    </Stack>
  );
};

function LandingPage() {
  const { t } = useLanguageHook();
  const theme = useTheme();
  const providerItems = useAppSelector((state: ApplicationState) => state.data.providers);
  const availableProviders = providerItems.filter((card) => !card.hidden);

  const visualizations = [
    { title: t.LandingPage.TreemapTitle, text: t.LandingPage.TreemapText, image: asset("treemap.png") },
    { title: t.LandingPage.TimelineTitle, text: t.LandingPage.TimelineText, image: asset("timeline.png") },
    { title: t.LandingPage.SunBurstTitle, text: t.LandingPage.SunBurstText, image: asset("sunburst.png") },
    { title: t.LandingPage.SparklinesTitle, text: t.LandingPage.SparklinesText, image: asset("sparkline.png") },
    { title: t.LandingPage.PieChartTitle, text: t.LandingPage.PieChartText, image: asset("piechart.png") },
    {
      title: t.LandingPage.NetworkGraphTitle,
      text: t.LandingPage.NetworkGraphText,
      image: asset("contactnetwork.png"),
    },
    { title: t.LandingPage.BarChartTitle, text: t.LandingPage.BarChartText, image: asset("barchart.png") },
  ];

  const trust = [
    { icon: <CloudOffOutlined />, text: t.LandingPage.TrustLocal },
    { icon: <LockOutlined />, text: t.LandingPage.TrustNoAccount },
    { icon: <SchoolOutlined />, text: t.LandingPage.TrustResearch },
  ];

  return (
    <main>
      {/* Hero */}
      <Box
        sx={{ position: "relative", zIndex: 3, pt: { xs: 5, md: 8 }, pb: 6 }}
        style={{
          backgroundImage: `linear-gradient(180deg, ${theme.palette.background.paper}00 0%, ${theme.palette.background.paper}00 85%, ${theme.palette.background.paper}ff 100%), url(${theme.landingPage.backgroundImage})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            fontSize={{ xs: "h3.fontSize", sm: "h2.fontSize", md: 40 }}
            align="center"
            color="text.primary"
            gutterBottom
            sx={{ textWrap: "balance" }}
          >
            {t.LandingPage.Header}
          </Typography>
          <Typography
            variant="h2"
            component="p"
            fontSize={{ xs: "h6.fontSize", sm: "h5.fontSize" }}
            align="center"
            color="text.secondary"
            sx={{ textWrap: "balance", maxWidth: "48ch", mx: "auto" }}
            paragraph
          >
            {t.LandingPage.Subtitle}
          </Typography>

          <HeroMedia />

          <Typography
            align="center"
            color="text.secondary"
            sx={{ pt: 3, maxWidth: "60ch", mx: "auto", textWrap: "pretty" }}
          >
            {t.LandingPage.ExplainTool}
          </Typography>

          {/* Two doors: bring your own export, or look around first. */}
          <Stack
            sx={{ pt: 4 }}
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              component={Link}
              to={routeConfig.Application}
              size="large"
              variant="contained"
              color="primary"
              sx={ctaSx}
            >
              {t.LandingPage.ButtonGo} <ChevronRight />
            </Button>
            <Button
              component={Link}
              to={DEMO_ROUTE}
              size="large"
              variant="outlined"
              color="inherit"
              sx={{ textTransform: "none", fontSize: "1.1rem" }}
            >
              {t.LandingPage.ButtonDemo}
            </Button>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1, sm: 4 }}
            justifyContent="center"
            alignItems="center"
            sx={{ pt: 4, color: "text.secondary" }}
          >
            {trust.map((item) => (
              <Stack key={item.text} direction="row" spacing={1} alignItems="center">
                {item.icon}
                <Typography variant="body2">{item.text}</Typography>
              </Stack>
            ))}
          </Stack>
        </Container>
      </Box>

      <Box sx={{ bgcolor: "background.paper" }}>
        {/* Providers */}
        <Container id="providers" sx={sectionSx} maxWidth="md">
          <Stack direction="column" gap={3}>
            <Typography variant="h2" align="center">
              {t.LandingPage.ProvidersAvailable}
            </Typography>
            <Typography align="center">{t.LandingPage.ProvidersAvailableSubtitle}</Typography>
            <Grid container spacing={1} justifyContent="center">
              {availableProviders.map((provider) => (
                <Grid item key={provider.id}>
                  <Button
                    component={Link}
                    to={`${routeConfig.Application}/data/${provider.id}`}
                    variant="contained"
                    sx={{ color: "white", backgroundColor: "#393b3b", textTransform: "none" }}
                  >
                    {provider.name}
                  </Button>
                </Grid>
              ))}
              <Grid item>
                <Button
                  component={Link}
                  to={routeConfig.Application}
                  variant="contained"
                  sx={{ color: "white", backgroundColor: "#393b3b", textTransform: "none", px: 2 }}
                >
                  {t.LandingPage.ProvidersCustom}
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </Container>

        {/* Views: grid of visualizations */}
        <Container id="views" sx={sectionSx} maxWidth="lg">
          <Typography variant="h2" align="center" gutterBottom>
            {t.LandingPage.ViewsTitle}
          </Typography>
          <Typography align="center" color="text.secondary" sx={{ maxWidth: "65ch", mx: "auto", pb: 4 }}>
            {t.LandingPage.ViewsSubtitle}
          </Typography>
          <Grid container spacing={3} alignItems="stretch">
            {visualizations.map((v) => (
              <VizCard key={v.title} {...v} />
            ))}
          </Grid>
        </Container>

        {/* Research — promoted out of the footer */}
        <Container id="research" sx={sectionSx} maxWidth="md">
          <Typography variant="h2" align="center" gutterBottom>
            {t.LandingPage.Publications}
          </Typography>
          <Typography>{t.LandingPage.PublicationsText}</Typography>

          {PUBLICATIONS.map((paper) => (
            <PublicationEntry key={paper.url} paper={paper} />
          ))}

          <Box sx={{ pt: 4 }}>
            <Typography component="summary" variant="h5" sx={{ cursor: "pointer" }}>
              {t.LandingPage.ThesesTitle}
            </Typography>
            <Box component="ul" sx={{ pt: 1 }}>
              {THESES.map((thesis) => (
                <li key={thesis}>
                  <Typography variant="body2">{thesis}</Typography>
                </li>
              ))}
            </Box>
          </Box>
        </Container>

        {/* FAQ */}
        <Container sx={{ pb: 10 }} maxWidth="lg">
          <Typography align="center" variant="h2" gutterBottom>
            {t.LandingPage.FAQ.Title}
          </Typography>

          <Stack sx={{ pt: 3, maxWidth: 900, mx: "auto" }} spacing={1}>
            {t.LandingPage.FAQ.items.map((item) => (
              <Accordion key={item.question} disableGutters>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls={`${item.question}-content`}
                  id={`${item.question}-header`}
                >
                  <Typography fontWeight={600}>{item.question}</Typography>
                </AccordionSummary>

                <AccordionDetails>
                  <Typography>{item.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Container>

        {/* Final CTA */}
        <Container sx={{ pb: 10, pt: 10 }} maxWidth="lg">
          <Typography align="center" variant="h2" gutterBottom>
            {t.LandingPage.ExploreNow}
          </Typography>
          <Stack
            sx={{ pt: 3 }}
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              component={Link}
              to={routeConfig.Application}
              size="large"
              variant="contained"
              color="primary"
              sx={ctaSx}
            >
              {t.LandingPage.ButtonGo} <ChevronRight />
            </Button>
            <Button
              component={Link}
              to={DEMO_ROUTE}
              size="large"
              variant="outlined"
              color="inherit"
              sx={{ textTransform: "none", fontSize: "1.1rem" }}
            >
              {t.LandingPage.ButtonDemo}
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Transition */}
      <Box sx={{ bgcolor: "background.paper", mb: -1, lineHeight: 0 }}>
        <svg width="100%" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1920 100">
          <title>Footer transition</title>
          <path fill={theme.palette.common.white} d="M0,0c0,0,934.4,93.4,1920,0v100.1H0L0,0z" />
        </svg>
      </Box>

      {/* Footer: partners, contact, legal only */}
      <Box component="footer" sx={{ bgcolor: "common.white", color: "grey.900", pt: 4, pb: 6 }}>
        <Container sx={sectionSx} maxWidth="md">
          <Typography variant="h2" align="center" gutterBottom>
            {t.LandingPage.ResearchGroup}
          </Typography>

          {/** biome-ignore lint/security/noDangerouslySetInnerHtml: we kinda want to inject the <a> here */}
          <Typography paragraph component="div" dangerouslySetInnerHTML={{ __html: t.LandingPage.AboutDevelopment }} />

          <Typography variant="h5" gutterBottom>
            {t.LandingPage.CollaborationTitle}
          </Typography>
          <Typography paragraph>{t.LandingPage.CollaborationText}</Typography>
          <Button href={`mailto:${EMAIL_LINK}`} endIcon={<Email fontSize="small" />} sx={{ textTransform: "none" }}>
            {EMAIL_LINK}
          </Button>
          <Button
            href={IVA_URL}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNew fontSize="small" />}
            sx={{ mt: 0, textTransform: "none" }}
          >
            {t.LandingPage.MoreDemos}
          </Button>
        </Container>

        <Container maxWidth="md">
          <Typography variant="h2" align="center" gutterBottom>
            {t.LandingPage.Acknowledgements}
          </Typography>
          <Typography>{t.LandingPage.AcknowledgementsText}</Typography>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="center" alignItems="center">
            {[
              { href: "https://www.igd.fraunhofer.de/", src: IgdSVG, alt: "Fraunhofer IGD" },
              { href: "https://www.tu-darmstadt.de/", src: TudSVG, alt: "TU Darmstadt" },
              { href: "https://www.athene-center.de/", src: AtheneSVG, alt: "ATHENE Center" },
            ].map((logo) => (
              <a key={logo.href} target="_blank" rel="noopener noreferrer" href={logo.href}>
                <img alt={logo.alt} src={logo.src} style={{ height: "120px", width: "260px", objectFit: "contain" }} />
              </a>
            ))}
          </Stack>

          <Stack direction="row" spacing={2} justifyContent="center" sx={{ pt: 4 }}>
            <MuiLink sx={{ p: "6px" }} color="inherit" href="https://www.igd.fraunhofer.de/">
              {`© 2020-${new Date().getFullYear()} Fraunhofer IGD`}
            </MuiLink>
            <Button component={Link} to={routeConfig.Imprint} color="inherit">
              {t.UI.Imprint}
            </Button>
            <Button component={Link} to={routeConfig.Dsgvo} color="inherit">
              {t.UI.PrivacyPolicy}
            </Button>
          </Stack>
        </Container>
      </Box>
    </main>
  );
}

export { LandingPage };
